/**
 * Key Storage Service
 *
 * Provides secure browser-based storage for quantum cryptography keys.
 * Uses IndexedDB for persistence with localStorage fallback.
 */

import type { MasterKeyData } from '@/types/quantum';

const KEY_DB_NAME = 'QuantumSafeVaultKeys';
const KEY_DB_VERSION = 1;
const MASTER_KEY_ID = 'master-key';
const STORAGE_KEY_PREFIX = 'quantum-safe-vault-keys';

export interface StoredKeyData {
  id: string;
  keyData: MasterKeyData;
  createdAt: number;
  lastUsed: number;
  algorithm: string;
  securityLevel: number;
}

/**
 * Secure key storage for browser environment
 */
export class KeyStorage {
  private db: IDBDatabase | null = null;
  private useFallback = false;

  constructor() {
    // Initialize lazily when first needed
  }

  /**
   * Initialize IndexedDB for key storage
   */
  private async initializeDB(): Promise<void> {
    return new Promise((resolve) => {
      console.log('KeyStorage: Initializing IndexedDB for keys...');

      const request = indexedDB.open(KEY_DB_NAME, KEY_DB_VERSION);

      request.onerror = () => {
        console.error('KeyStorage: Failed to open IndexedDB', request.error);
        console.log('KeyStorage: Falling back to localStorage');
        this.useFallback = true;
        resolve();
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        console.log('KeyStorage: IndexedDB opened successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains('keys')) {
          const keyStore = db.createObjectStore('keys', { keyPath: 'id' });
          keyStore.createIndex('createdAt', 'createdAt', { unique: false });
          console.log('KeyStorage: Created keys store');
        }
      };
    });
  }

  /**
   * Store the master key
   */
  async storeMasterKey(keyData: MasterKeyData, algorithm: string, securityLevel: number): Promise<void> {
    await this.ensureDBReady();

    const storedKey: StoredKeyData = {
      id: MASTER_KEY_ID,
      keyData,
      createdAt: Date.now(),
      lastUsed: Date.now(),
      algorithm,
      securityLevel
    };

    console.log('KeyStorage: Storing master key...', {
      algorithm,
      securityLevel,
      usingFallback: this.useFallback
    });

    if (this.useFallback) {
      return this.storeFallback(storedKey);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['keys'], 'readwrite');
      const store = transaction.objectStore('keys');
      const request = store.put(storedKey);

      request.onsuccess = () => {
        console.log('KeyStorage: Master key stored successfully');
        resolve();
      };

      request.onerror = () => {
        console.error('KeyStorage: Failed to store master key', request.error);
        reject(new Error('Failed to store master key'));
      };
    });
  }

  /**
   * Retrieve the master key
   */
  async getMasterKey(): Promise<MasterKeyData | null> {
    await this.ensureDBReady();

    console.log('KeyStorage: Retrieving master key...');

    if (this.useFallback) {
      return this.getFallback();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['keys'], 'readonly');
      const store = transaction.objectStore('keys');
      const request = store.get(MASTER_KEY_ID);

      request.onsuccess = () => {
        const result = request.result as StoredKeyData | undefined;
        if (result) {
          console.log('KeyStorage: Master key retrieved successfully');
          // Update last used timestamp
          this.updateLastUsed(MASTER_KEY_ID);
          resolve(result.keyData);
        } else {
          console.log('KeyStorage: No master key found');
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('KeyStorage: Failed to retrieve master key', request.error);
        reject(new Error('Failed to retrieve master key'));
      };
    });
  }

  /**
   * Check if master key exists
   */
  async hasMasterKey(): Promise<boolean> {
    const key = await this.getMasterKey();
    return key !== null;
  }

  /**
   * Clear the master key (for testing/reset)
   */
  async clearMasterKey(): Promise<void> {
    await this.ensureDBReady();

    console.log('KeyStorage: Clearing master key...');

    if (this.useFallback) {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}-${MASTER_KEY_ID}`);
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['keys'], 'readwrite');
      const store = transaction.objectStore('keys');
      const request = store.delete(MASTER_KEY_ID);

      request.onsuccess = () => {
        console.log('KeyStorage: Master key cleared');
        resolve();
      };

      request.onerror = () => {
        console.error('KeyStorage: Failed to clear master key', request.error);
        reject(new Error('Failed to clear master key'));
      };
    });
  }

  /**
   * Update last used timestamp
   */
  private async updateLastUsed(keyId: string): Promise<void> {
    if (this.useFallback) return;

    const transaction = this.db!.transaction(['keys'], 'readwrite');
    const store = transaction.objectStore('keys');
    const getRequest = store.get(keyId);

    getRequest.onsuccess = () => {
      const data = getRequest.result as StoredKeyData;
      if (data) {
        data.lastUsed = Date.now();
        store.put(data);
      }
    };
  }

  /**
   * LocalStorage fallback methods
   */
  private async storeFallback(keyData: StoredKeyData): Promise<void> {
    try {
      const key = `${STORAGE_KEY_PREFIX}-${keyData.id}`;
      localStorage.setItem(key, JSON.stringify(keyData));
      console.log('KeyStorage: Stored key in localStorage fallback');
    } catch (error) {
      console.error('KeyStorage: LocalStorage fallback failed', error);
      throw new Error('Failed to store key in localStorage');
    }
  }

  private async getFallback(): Promise<MasterKeyData | null> {
    try {
      const key = `${STORAGE_KEY_PREFIX}-${MASTER_KEY_ID}`;
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data) as StoredKeyData;
        console.log('KeyStorage: Retrieved key from localStorage fallback');
        return parsed.keyData;
      }
      return null;
    } catch (error) {
      console.error('KeyStorage: LocalStorage retrieval failed', error);
      return null;
    }
  }

  /**
   * Ensure database is ready
   */
  private async ensureDBReady(): Promise<void> {
    if (!this.db && !this.useFallback) {
      await this.initializeDB();
    }
  }

  /**
   * Debug function to inspect stored keys
   */
  async debugKeyStorage(): Promise<void> {
    await this.ensureDBReady();

    console.log('=== KeyStorage DEBUG ===');
    console.log('Using Fallback:', this.useFallback);

    if (this.useFallback) {
      console.log('Storage Method: LocalStorage');
      const key = `${STORAGE_KEY_PREFIX}-${MASTER_KEY_ID}`;
      const data = localStorage.getItem(key);
      console.log('Stored Master Key:', data ? JSON.parse(data) : null);
    } else {
      console.log('Storage Method: IndexedDB');
      const transaction = this.db!.transaction(['keys'], 'readonly');
      const store = transaction.objectStore('keys');
      const request = store.getAll();

      return new Promise((resolve) => {
        request.onsuccess = () => {
          console.log('Stored Keys:', request.result);
          console.log('=== END DEBUG ===');
          resolve();
        };
      });
    }
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

/**
 * Create singleton instance
 */
let keyStorageInstance: KeyStorage | null = null;

export function getKeyStorage(): KeyStorage {
  if (!keyStorageInstance) {
    keyStorageInstance = new KeyStorage();
  }
  return keyStorageInstance;
}

/**
 * Debug function available globally
 */
export async function debugKeyStorage(): Promise<void> {
  const storage = getKeyStorage();
  await storage.debugKeyStorage();
}

/**
 * Reset function to clear all stored keys - available globally
 */
export async function resetKeyStorage(): Promise<void> {
  const storage = getKeyStorage();
  await storage.clearMasterKey();
  console.log('KeyStorage: Master key cleared');
}

// Make available on window for browser console debugging
if (typeof window !== 'undefined') {
  (window as any).debugKeyStorage = debugKeyStorage;
  (window as any).resetKeyStorage = resetKeyStorage;
}

export default KeyStorage;